import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { TPromoBannerBase } from '@onesource/schemas';
import { isRichHtmlEmpty, sanitizeRichHtml, stripHtml } from '@onesource/utils';
import { FC } from 'react';
import { MdChevronRight } from 'react-icons/md';

import { useGetByBreakpoint } from '../../hooks';
import { resolvePromoBannerBackground } from '../promo-banner-colors';
import { promoBannerRichTextSx } from '../promo-banner-rich-text';
import desktopImg from './desktop-img.png';
import mobileImg from './mobile-img.png';

export interface PromoBannerWithLaurenImageProps {
  banner: TPromoBannerBase;
}

/**
 * Bump & Birth promo banner layout (see the RYC® 12-Week design).
 *
 * A wide gradient card with the bundled Lauren photos: on desktop the headshot
 * sits flush left with the headline, review pill and CTA in a row; on mobile
 * the seated pose fills the right column with the CTA tucked under it.
 */
export const PromoBannerWithLaurenImage: FC<PromoBannerWithLaurenImageProps> = ({ banner }) => {
  // The whole layout (grid areas, images, fonts) switches at `md`, so the text
  // content must switch on the same breakpoint — not useGetByDevice's `sm`.
  const isMobileLayout = useGetByBreakpoint('down', 'md');

  const isExternal = /^https?:\/\//i.test(banner.buttonUrl);
  // Mobile copies are optional — fall back to the desktop content when unset.
  const title =
    isMobileLayout && banner.mobileTitle && !isRichHtmlEmpty(banner.mobileTitle) ? banner.mobileTitle : banner.title;
  const description =
    isMobileLayout && banner.mobileDescription && !isRichHtmlEmpty(banner.mobileDescription)
      ? banner.mobileDescription
      : banner.description;
  // Admins can hide the review pill per device without deleting the copy.
  // `!== false` keeps banners visible while an older API omits the flags.
  const isDesktopDescriptionActive = banner.isDescriptionActive !== false;
  const isMobileDescriptionActive = banner.isMobileDescriptionActive !== false;
  const isDescriptionVisible = isMobileLayout ? isMobileDescriptionActive : isDesktopDescriptionActive;
  // Title is rich HTML; alt must be plain text.
  const imageAlt = stripHtml(title);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateAreas: { xs: '"text img" "text cta"', md: '"img text cta"' },
        gridTemplateColumns: { xs: '1fr auto', md: 'auto 1fr auto' },
        gridTemplateRows: { xs: '1fr auto', md: '1fr' },
        alignItems: { md: 'center' },
        columnGap: { xs: 1.25, md: 2.5 },
        rowGap: { xs: isMobileDescriptionActive ? 1 : 0, md: 0 },
        p: { xs: 1.5, md: 0 },
        px: { md: 2 },
        height: { xs: 'auto', md: !isDesktopDescriptionActive ? 78 : 98 },
        width: '100%',
        borderRadius: { xs: '12px', md: '16px' },
        overflow: 'hidden',
        background: resolvePromoBannerBackground(banner.backgroundColor),
      }}
    >
      <Box
        component="img"
        src={desktopImg}
        alt={imageAlt}
        sx={{
          display: { xs: 'none', md: 'block' },
          gridArea: 'img',
          width: 106,
          objectFit: 'cover',
          objectPosition: 'top',
          height: !isDesktopDescriptionActive ? 78 : 98,
        }}
      />

      <Box
        component="img"
        src={isMobileDescriptionActive ? mobileImg : desktopImg}
        alt={imageAlt}
        sx={{
          display: { xs: 'block', md: 'none' },
          gridArea: 'img',
          width: isMobileDescriptionActive ? 51 : 59,
          height: '100%',
          maxHeight: isMobileDescriptionActive ? 83 : 47,
          objectFit: 'contain',
          objectPosition: 'bottom',
        }}
      />

      <Stack
        spacing={1}
        sx={{
          gridArea: 'text',
          minWidth: 0,
          justifyContent: 'center',
          py: { md: 1 },
        }}
      >
        <Box
          sx={{
            ...promoBannerRichTextSx,
            color: theme => theme.palette.common.white,
            fontWeight: 700,
            fontSize: { xs: 10, md: 14 },
            lineHeight: { xs: '19px', md: '20px' },
            '& span': { fontSize: { xs: 10, md: 14 } },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(title) }}
        />

        {isDescriptionVisible && !isRichHtmlEmpty(description) && (
          <Box
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '4px',
              px: { xs: 0.9, md: 1.5 },
              py: 0.5,
            }}
          >
            <Box
              sx={{
                ...promoBannerRichTextSx,
                fontStyle: 'italic',
                color: '#000',
                fontSize: { xs: '9px', md: '9px' },
                lineHeight: 1.2,
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(description) }}
            />
          </Box>
        )}
      </Stack>

      <Button
        variant="soft"
        href={banner.buttonUrl}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        endIcon={<MdChevronRight size={20} />}
        sx={{
          gridArea: 'cta',
          alignSelf: { xs: 'end', md: 'center' },
          justifySelf: 'end',
          minHeight: { xs: '32px', md: '38px' },
          maxHeight: { xs: '32px', md: '38px' },
          borderRadius: '8px',
          color: '#fff !important',
          borderColor: theme => theme.palette.common.white,
          fontSize: { xs: '12px', md: '13px' },
          fontWeight: 700,
          px: 2,
          whiteSpace: 'nowrap',
          '& .MuiButton-endIcon': { ml: 0.25 },
          '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255, 255, 255, 0.14)' },
        }}
      >
        {banner.buttonLabel}
      </Button>
    </Box>
  );
};
